"use client";

import React from "react";
import { useLocale, useTranslations } from "next-intl";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpRight,
  Check,
  Globe,
  Info,
  Plus,
  Router,
  Wifi,
} from "lucide-react";
import { Link } from "@/i18n/routing";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { InternetPlan } from "@/lib/fetch/internet";
import {
  formatInternetPrice,
  formatInternetSpeed,
  getRunningMonthlyCents,
  isMonthlyPlan,
  isSymmetricPlan,
} from "./internet-plan-math";
import { INTERNET_CONNECTION_ICONS } from "./internet-meta";
import { getSpeedFillPercent } from "./InternetSpeedMeter";

type Props = {
  plan: InternetPlan;
  onOpenDetails: (id: string) => void;
  compare?: {
    selected: boolean;
    disabled: boolean;
    onToggle: (id: string) => void;
  };
  /** Hide the provider line where the page is already about the provider. */
  showProvider?: boolean;
  /** Hide the connection label inside a group already titled by connection. */
  showConnection?: boolean;
  /** Short reasons shown under the facts, e.g. from the finder. */
  reasons?: string[];
  className?: string;
};

type SpecTile = {
  key: string;
  label: string;
  icon?: typeof ArrowDown;
  value: string;
  /** Speed tiles carry a log-scaled bar so a list reads at a glance. */
  fill?: { percent: number; className: string } | null;
  badge?: string | null;
};

/**
 * One plan as a spec card: who sells it and the monthly bill on top, then the
 * four facts people compare (download, upload, contract, installation) as
 * equal tiles that line up from card to card, then the next steps.
 */
const InternetPlanCard: React.FC<Props> = ({
  plan,
  onOpenDetails,
  compare,
  showProvider = true,
  showConnection = true,
  reasons = [],
  className,
}) => {
  const t = useTranslations("internet");
  const locale = useLocale();
  const Icon = INTERNET_CONNECTION_ICONS[plan.connectionType];
  const running = getRunningMonthlyCents(plan);
  const rentalCents = plan.equipmentMonthlyFeeCents ?? 0;
  const networkFeeCents = plan.networkMonthlyFeeCents ?? 0;

  const speedTile = (
    key: string,
    label: string,
    icon: typeof ArrowDown,
    mbps: number | null,
    fillClassName: string,
    badge?: string | null,
  ): SpecTile => ({
    key,
    label,
    icon,
    value:
      mbps !== null ? formatInternetSpeed(mbps, locale) : t("plan.not-stated"),
    fill:
      mbps !== null
        ? { percent: getSpeedFillPercent(mbps), className: fillClassName }
        : null,
    badge,
  });

  const tiles: SpecTile[] = [
    speedTile(
      "down",
      t("plan.download"),
      ArrowDown,
      plan.downloadMbps,
      "bg-accent",
    ),
    speedTile(
      "up",
      t("plan.upload"),
      ArrowUp,
      plan.uploadMbps,
      "bg-lime-500",
      isSymmetricPlan(plan) ? t("plan.symmetric") : null,
    ),
    {
      key: "term",
      label: t("plan.spec.term"),
      value: isMonthlyPlan(plan)
        ? t("plan.spec.monthly")
        : plan.minimumContractMonths
          ? t("plan.spec.months", { months: plan.minimumContractMonths })
          : t("plan.not-stated"),
    },
    {
      key: "install",
      label: t("detail.facts.installation"),
      value: t(`installation.${plan.installationMethod}`),
    },
  ];

  const extras = [
    plan.ownRouterSupported === true && {
      key: "router",
      icon: Router,
      label: t("plan.own-router"),
    },
    plan.staticIp === true && {
      key: "ip",
      icon: Globe,
      label: t("plan.static-ip"),
    },
    plan.wifiStandard && {
      key: "wifi",
      icon: Wifi,
      label: plan.wifiStandard,
    },
  ].filter(Boolean) as { key: string; icon: typeof Wifi; label: string }[];

  return (
    <article
      className={cn(
        "@container flex h-full min-w-0 flex-col rounded-2xl border border-border bg-background shadow-sm transition-colors hover:border-zinc-300",
        compare?.selected && "border-zinc-400 ring-2 ring-secondary",
        className,
      )}
    >
      <div className="flex h-full flex-col gap-4 p-5">
        {/* Identity and the monthly bill */}
        <div className="flex flex-col gap-3 @lg:flex-row @lg:items-start @lg:justify-between @lg:gap-6">
          <div className="flex min-w-0 items-start gap-3">
            <span className="flex size-10 flex-none items-center justify-center rounded-xl bg-secondary">
              <Icon className="size-5" aria-hidden />
            </span>
            <div className="flex min-w-0 flex-col gap-0.5">
              {showProvider && (
                <Link
                  href={`/resource/internet/${plan.provider.slug}`}
                  title={t("plan.provider-link-title", {
                    provider: plan.provider.name,
                  })}
                  className="w-fit truncate text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-blue-700"
                >
                  {plan.provider.name}
                </Link>
              )}
              <h3 className="text-base font-semibold leading-snug @lg:text-lg">
                {plan.name}
              </h3>
              {(showConnection || plan.network) && (
                <p className="text-xs leading-5 text-muted-foreground">
                  {showConnection &&
                    t(`connection-type.${plan.connectionType}`)}
                  {showConnection && plan.network && (
                    <span aria-hidden> · </span>
                  )}
                  {plan.network && (
                    <>
                      {t("plan.via-network")}{" "}
                      <Link
                        href={`/resource/internet/network/${plan.network.slug}`}
                        title={t("plan.network-link-title", {
                          network: plan.network.name,
                        })}
                        className="font-medium text-foreground hover:text-blue-700 hover:underline"
                      >
                        {plan.network.name}
                      </Link>
                    </>
                  )}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-none flex-col gap-0.5 pl-[3.25rem] @lg:items-end @lg:pl-0 @lg:text-right">
            {running !== null ? (
              <>
                <p className="flex items-baseline gap-1">
                  <span className="text-2xl font-semibold tracking-tight tabular-nums">
                    {formatInternetPrice(running, locale)}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {t("plan.per-month")}
                  </span>
                </p>
                {plan.introMonthlyPriceCents !== null &&
                  plan.introDurationMonths && (
                    <p className="text-xs font-semibold text-emerald-700">
                      {t("plan.intro", {
                        price: formatInternetPrice(
                          plan.introMonthlyPriceCents,
                          locale,
                        ),
                        months: plan.introDurationMonths,
                      })}
                    </p>
                  )}
                {(rentalCents > 0 || networkFeeCents > 0) && (
                  <p className="max-w-64 text-xs text-muted-foreground">
                    {networkFeeCents > 0
                      ? plan.networkFeeVaries
                        ? t("plan.includes-network-fee-from", {
                            price: formatInternetPrice(networkFeeCents, locale),
                          })
                        : t("plan.includes-network-fee")
                      : t("plan.includes-rental", {
                          price: formatInternetPrice(rentalCents, locale),
                        })}
                  </p>
                )}
              </>
            ) : (
              <>
                <p className="text-base font-semibold">
                  {t("plan.price-on-check")}
                </p>
                <p className="max-w-64 text-xs text-muted-foreground">
                  {t("plan.price-on-check-note")}
                </p>
              </>
            )}
          </div>
        </div>

        {/* The four facts, equal tiles so values line up down a list */}
        <dl className="grid grid-cols-2 gap-2 @xl:grid-cols-4">
          {tiles.map((tile) => {
            const TileIcon = tile.icon;
            return (
              <div
                key={tile.key}
                className="flex min-w-0 flex-col gap-1 rounded-xl bg-slate-50 px-3 py-2.5"
              >
                <dt className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
                  {TileIcon && (
                    <TileIcon className="size-3.5 flex-none" aria-hidden />
                  )}
                  <span className="truncate">{tile.label}</span>
                </dt>
                <dd className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-semibold leading-snug tabular-nums @md:text-base">
                  {tile.value}
                  {tile.badge && (
                    <span className="rounded-full bg-secondary px-1.5 py-px text-[10px] font-semibold text-secondary-foreground">
                      {tile.badge}
                    </span>
                  )}
                </dd>
                {tile.fill !== undefined && (
                  <span
                    className="relative mt-0.5 h-1.5 overflow-hidden rounded-full bg-zinc-200"
                    aria-hidden
                  >
                    {tile.fill && (
                      <span
                        className={cn(
                          "absolute inset-y-0 left-0 rounded-full",
                          tile.fill.className,
                        )}
                        style={{ width: `${tile.fill.percent}%` }}
                      />
                    )}
                  </span>
                )}
              </div>
            );
          })}
        </dl>

        {extras.length > 0 && (
          <ul className="-mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            {extras.map((extra) => {
              const ExtraIcon = extra.icon;
              return (
                <li key={extra.key} className="flex items-center gap-1.5">
                  <ExtraIcon className="size-3.5" aria-hidden />
                  {extra.label}
                </li>
              );
            })}
          </ul>
        )}

        {reasons.length > 0 && (
          <ul className="flex flex-col gap-1.5 rounded-xl bg-lime-50 px-3.5 py-3 @2xl:flex-row @2xl:flex-wrap @2xl:gap-x-6">
            {reasons.map((reason) => (
              <li
                key={reason}
                className="flex items-start gap-2 text-sm leading-5 text-foreground"
              >
                <Check
                  className="mt-0.5 size-4 flex-none text-emerald-600"
                  aria-hidden
                />
                {reason}
              </li>
            ))}
          </ul>
        )}

        {/* Next steps */}
        <div className="mt-auto flex flex-col gap-3 border-t border-border pt-4 @lg:flex-row @lg:items-center @lg:justify-between">
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenDetails(plan.id)}
              title={t("plan.details-title", {
                plan: plan.name,
                provider: plan.provider.name,
              })}
              className="flex-1 @lg:flex-none"
            >
              <Info className="size-4" />
              {t("plan.details")}
            </Button>
            {compare && (
              <Button
                type="button"
                variant={compare.selected ? "secondary" : "outline"}
                aria-pressed={compare.selected}
                disabled={compare.disabled && !compare.selected}
                onClick={() => compare.onToggle(plan.id)}
                title={
                  compare.disabled && !compare.selected
                    ? t("plan.compare-full")
                    : t("plan.compare-title", { plan: plan.name })
                }
                className="flex-1 @lg:flex-none"
              >
                {compare.selected ? (
                  <Check className="size-4" />
                ) : (
                  <Plus className="size-4" />
                )}
                {compare.selected ? t("plan.compare-added") : t("plan.compare")}
              </Button>
            )}
          </div>

          <a
            href={plan.postcodeCheckUrl}
            target="_blank"
            rel="noreferrer nofollow"
            title={t("plan.check-address-title", {
              provider: plan.provider.name,
            })}
            className="inline-flex min-h-10 w-fit items-center gap-1 self-center text-sm font-semibold text-blue-700 hover:underline @lg:self-auto"
          >
            {t("plan.check-address")}
            <ArrowUpRight className="size-4" aria-hidden />
          </a>
        </div>
      </div>
    </article>
  );
};

export default InternetPlanCard;
